import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Space, Tooltip, message } from "antd";
import { AuditOutlined, RollbackOutlined, SendOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DayApprovalState,
  dailyApprovalApis,
  emptyState,
  normaliseState,
} from "../../../api/DailyApprovals";
import { ApprovalStatusPill, RemarksModal, SubmitDayModal, fmtDay } from "./approvalUi";

// Daily Execution's view of approvals: each date's status, the rejection
// remarks the site must act on, and Submit / Recall / Open review.

/** Approval states for a date range of one project; `reload` refetches. */
export const useDayApprovalStates = (
  projectId: number | string | undefined,
  fromDate: string,
  toDate: string,
  reloadToken = 0
) => {
  const [states, setStates] = useState<Map<string, DayApprovalState>>(new Map());
  const [configured, setConfigured] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!projectId || !fromDate || !toDate) {
      setStates(new Map());
      return;
    }
    let cancelled = false;
    dailyApprovalApis
      .states(projectId, fromDate, toDate)
      .then((res) => {
        if (cancelled || res?.status !== true) return;
        const map = new Map<string, DayApprovalState>();
        (res.data?.states || []).forEach((raw: any) => {
          const state = normaliseState(raw);
          map.set(state.reportDate, state);
        });
        setStates(map);
        setConfigured(res.data?.approvalsConfigured === true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [projectId, fromDate, toDate, reloadToken, tick]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { states, configured, reload };
};

/** Compact controls for one date: status + Submit / Recall / Review. */
export const DayApprovalActions: React.FC<{
  projectId: number | string;
  date: string;
  state?: DayApprovalState;
  size?: "small" | "middle";
  onChanged: () => void;
}> = ({ projectId, date, state, size = "small", onChanged }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [submitOpen, setSubmitOpen] = useState(false);
  const [recallOpen, setRecallOpen] = useState(false);
  const current = state ?? emptyState(Number(projectId), date);
  const openReview = () =>
    navigate(`/pms/project/daily-approvals/review/${projectId}/${date}`, { state: location.state });

  return (
    <>
      <Space size={6} wrap onClick={(e) => e.stopPropagation()}>
        {current.status && <ApprovalStatusPill state={current} />}
        {current.canSubmit && (
          <Tooltip title={current.status === "Rejected" ? "Resubmit after correcting" : "Lock the day and send it for review"}>
            <Button size={size} icon={<SendOutlined />} onClick={() => setSubmitOpen(true)}>
              {current.status === "Rejected" || current.status === "Recalled" || current.status === "Reopened" ? "Resubmit" : "Submit day"}
            </Button>
          </Tooltip>
        )}
        {current.canRecall && (
          <Button size={size} icon={<RollbackOutlined />} onClick={() => setRecallOpen(true)}>
            Recall
          </Button>
        )}
        {current.dprId > 0 && (
          <Button size={size} type="link" icon={<AuditOutlined />} onClick={openReview}>
            Review
          </Button>
        )}
      </Space>
      <SubmitDayModal
        open={submitOpen}
        projectId={projectId}
        reportDate={date}
        onCancel={() => setSubmitOpen(false)}
        onDone={() => {
          setSubmitOpen(false);
          onChanged();
        }}
      />
      <RemarksModal
        open={recallOpen}
        title={`Recall ${fmtDay(date)}`}
        intro="The day unlocks so it can be corrected, and nobody reviews it until it is submitted again."
        okText="Recall"
        minLength={5}
        placeholder="Why are you recalling the day?"
        onCancel={() => setRecallOpen(false)}
        onSubmit={async (remarks) => {
          const res = await dailyApprovalApis.recall({ dprId: current.dprId, round: current.round, remarks });
          if (res?.status !== true) {
            message.error(res?.message || "Could not recall the day");
            return false;
          }
          message.success(res.data?.message || "Day recalled");
          onChanged();
          return true;
        }}
      />
    </>
  );
};

/** The full bar above a sheet: status, the site's to-do after a rejection, actions. */
export const DayApprovalBar: React.FC<{
  projectId: number | string;
  date: string;
  state?: DayApprovalState;
  onChanged: () => void;
  /**
   * Drop the card and the date, and render just the actions — for a surface that
   * already has its own strip with the date on it (Store-Stock), where a second
   * bordered band below the first would repeat the date and eat the room the
   * sheet needs.
   */
  inline?: boolean;
}> = ({ projectId, date, state, onChanged, inline }) => {
  const current = state ?? emptyState(Number(projectId), date);
  const needsAction = ["Rejected", "Recalled", "Reopened"].includes(current.status);
  if (inline) {
    return (
      <span className="pms-appr-daybar-row" style={{ gap: 10 }}>
        <DayApprovalActions projectId={projectId} date={date} state={current} onChanged={onChanged} />
        {current.locked && (
          <span className="pms-appr-sub">
            {current.status === "Approved" ? "Approved — locked." : "Locked while under review."}
          </span>
        )}
        {needsAction && current.lastRemarks && (
          <Tooltip title={current.lastRemarks}>
            <span className="pms-appr-sub" style={{ color: "#cf1322" }}>
              {current.status} by {current.lastActionByName || "a reviewer"}
            </span>
          </Tooltip>
        )}
      </span>
    );
  }
  return (
    <div className="pms-appr-daybar">
      <div className="pms-appr-daybar-row">
        <span className="pms-appr-daybar-date">{fmtDay(date)}</span>
        <DayApprovalActions projectId={projectId} date={date} state={current} onChanged={onChanged} />
        {current.locked && (
          <span className="pms-appr-sub">
            {current.status === "Approved" ? "Approved days are locked." : "Locked while it is under review."}
          </span>
        )}
      </div>
      {needsAction && current.lastRemarks && (
        <Alert
          style={{ marginTop: 8 }}
          type={current.status === "Rejected" ? "error" : "warning"}
          showIcon
          message={`${current.status} by ${current.lastActionByName || "a reviewer"}${current.openNoteCount ? ` · ${current.openNoteCount} open note(s)` : ""}`}
          description={current.lastRemarks}
        />
      )}
    </div>
  );
};

/** Rejected days on a register page, each with its remarks. */
export const RejectedDaysAlert: React.FC<{
  states: Map<string, DayApprovalState>;
  onOpen: (date: string) => void;
}> = ({ states, onOpen }) => {
  const rejected = Array.from(states.values()).filter((state) => state.status === "Rejected");
  if (!rejected.length) return null;
  return (
    <Alert
      type="error"
      showIcon
      style={{ marginBottom: 16 }}
      message={`${rejected.length} day${rejected.length === 1 ? " was" : "s were"} rejected and need correcting`}
      description={
        <ul className="pms-appr-flag-list">
          {rejected.map((state) => (
            <li key={state.reportDate}>
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => onOpen(state.reportDate)}>
                {fmtDay(state.reportDate)}
              </Button>{" "}
              — {state.lastActionByName || "Reviewer"}: {state.lastRemarks}
              {state.openNoteCount ? ` (${state.openNoteCount} open note${state.openNoteCount === 1 ? "" : "s"})` : ""}
            </li>
          ))}
        </ul>
      }
    />
  );
};
